// contexts/AdminProvider.tsx
import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
    AdminContext,
    type AdminData,
    type AdminEntityData,
    type AdminEntityType,
    type DeletableEntityType,
    type SaveableEntityType,
} from './AdminContext';
import {
    associateAgency,
    associateCustomer,
    createAgency,
    createBroadcaster,
    createChannel,
    createCustomer,
    deleteAgency,
    deleteCustomer,
    deleteUser,
    getAssociatedAgencies,
    getAssociatedCustomers,
    getBroadcasters,
    getChannels,
    getCountries,
    getTaxonomies,
    getUnassociatedAgencies,
    getUnassociatedCustomers,
    getUsers,
    saveNewUser,
    updateAgency,
    updateBroadcaster,
    updateChannel,
    updateCustomer,
    updateUser,
} from '@/api';
import { notifyError } from '@/utils';
import axios from 'axios';
import { wasNotified } from '@/api/core/requestUtils';

const initialAdminData: AdminData = {
    users: [],
    agencies: [],
    broadcasters: [],
    clients: [],
    channels: [],
    unassociatedAgencies: [],
    unassociatedClients: [],
    countries: [],
    taxonomies: [],
};

export const AdminProvider = ({ children }: { children: ReactNode }) => {
    const [adminData, setAdminData] = useState<AdminData>(initialAdminData);
    // Reference-counted pending fetches — `loading` is derived from this,
    // so it stays true until EVERY in-flight fetch has settled. A simple
    // boolean flapped to false as soon as the fastest of several parallel
    // fetches returned, which broke readiness signals for consumers.
    const [pendingFetches, setPendingFetches] = useState(0);
    const loading = pendingFetches > 0;
    const incPending = useCallback(() => setPendingFetches((c) => c + 1), []);
    const decPending = useCallback(() => setPendingFetches((c) => Math.max(0, c - 1)), []);

    // One abort controller PER entity type — a shared ref across every entityType
    // caused parallel fetchData calls (e.g. ClientsTab firing clients +
    // taxonomies + countries + agencies at mount) to silently cancel each
    // other, leaving taxonomies/countries empty and flaking E2E tests.
    const abortControllersRef = useRef<Map<AdminEntityType, AbortController>>(new Map());

    const fetchData = useCallback(
        async (entityType: AdminEntityType) => {
            // Only cancel a PREVIOUS in-flight fetch for the SAME entity type.
            abortControllersRef.current.get(entityType)?.abort();
            const controller = new AbortController();
            abortControllersRef.current.set(entityType, controller);
            const signal = controller.signal;

            incPending();
            try {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                let data: any;
                switch (entityType) {
                    case 'users':
                        data = await getUsers({ signal });
                        setAdminData((prev) => ({ ...prev, users: data }));
                        break;
                    case 'agencies':
                        data = await getAssociatedAgencies({ signal });
                        setAdminData((prev) => ({ ...prev, agencies: data }));
                        break;
                    case 'broadcasters':
                        data = await getBroadcasters({ signal });
                        setAdminData((prev) => ({ ...prev, broadcasters: data }));
                        break;
                    case 'clients':
                        data = await getAssociatedCustomers({ signal });
                        setAdminData((prev) => ({ ...prev, clients: data }));
                        break;
                    case 'channels':
                        data = await getChannels({ signal });
                        setAdminData((prev) => ({ ...prev, channels: data }));
                        break;
                    case 'unassociatedAgencies':
                        data = await getUnassociatedAgencies({ signal });
                        setAdminData((prev) => ({ ...prev, unassociatedAgencies: data }));
                        break;
                    case 'unassociatedClients':
                        data = await getUnassociatedCustomers({ signal });
                        setAdminData((prev) => ({ ...prev, unassociatedClients: data }));
                        break;
                    case 'countries':
                        data = await getCountries({ signal });
                        setAdminData((prev) => ({ ...prev, countries: data }));
                        break;
                    case 'taxonomies':
                        data = await getTaxonomies({ signal });
                        setAdminData((prev) => ({ ...prev, taxonomies: data }));
                        break;
                    default:
                        console.warn(`AdminProvider: unknown entity type: ${entityType}`);
                }
                return data;
            } catch (error) {
                if (!axios.isCancel(error)) {
                    console.error(`AdminProvider: error fetching ${entityType}:`, error);
                    notifyError(`Unable to retrieve ${entityType}. Please try again.`);
                }
            } finally {
                // Clear the controller entry only if we're still the latest fetch for
                // this entity type — otherwise a newer call has already replaced
                // us and we'd be deleting its token.
                if (abortControllersRef.current.get(entityType) === controller) {
                    abortControllersRef.current.delete(entityType);
                }
                decPending();
            }
        },
        [incPending, decPending]
    );

    useEffect(() => {
        const controllers = abortControllersRef.current;
        return () => {
            controllers.forEach((controller) => controller.abort());
            controllers.clear();
        };
    }, []);

    const fetchCompleteEntitySet = useCallback(
        async (entityType: string) => {
            // No incPending here — the inner fetchData calls already track
            // their own pending state. Wrapping would double-count.
            try {
                const capitalised = entityType.charAt(0).toUpperCase() + entityType.slice(1);
                const [associated, unassociated] = await Promise.all([
                    fetchData(entityType as AdminEntityType),
                    fetchData(`unassociated${capitalised}` as AdminEntityType),
                ]);
                return { associated, unassociated };
            } catch (error) {
                console.error(`AdminProvider: error fetching complete ${entityType} set:`, error);
            }
        },
        [fetchData]
    );

    const handleSaveEntity = useCallback(
        async (entityData: AdminEntityData, entityType: SaveableEntityType, onSuccess?: () => void) => {
            incPending();

            try {
                if (entityData.existingId && entityType === 'client') {
                    await associateCustomer({ id: entityData.existingId as number });
                    await Promise.all([fetchData('clients'), fetchData('unassociatedClients')]);
                    onSuccess?.();
                    return;
                }

                if (entityData.existingId && entityType === 'agency') {
                    await associateAgency({ id: entityData.existingId as number });
                    await Promise.all([fetchData('agencies'), fetchData('unassociatedAgencies')]);
                    onSuccess?.();
                    return;
                }

                const isUpdate = Boolean(entityData.id);

                switch (entityType) {
                    case 'agency':
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        isUpdate ? await updateAgency(entityData as any) : await createAgency(entityData as any);
                        await Promise.all([fetchData('agencies'), fetchData('unassociatedAgencies')]);
                        break;
                    case 'broadcaster':
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        isUpdate
                            ? await updateBroadcaster(entityData as any)
                            : await createBroadcaster(entityData as any);
                        await fetchData('broadcasters');
                        break;
                    case 'channel':
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        isUpdate ? await updateChannel(entityData as any) : await createChannel(entityData as any);
                        await fetchData('channels');
                        break;
                    case 'client':
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        isUpdate ? await updateCustomer(entityData as any) : await createCustomer(entityData as any);
                        await Promise.all([fetchData('clients'), fetchData('unassociatedClients')]);
                        break;
                    case 'user':
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        isUpdate ? await updateUser(entityData as any) : await saveNewUser(entityData as any);
                        await fetchData('users');
                        break;
                    default:
                        console.error(`AdminProvider: unknown entity type: ${entityType}`);
                        notifyError(`Unable to save ${entityType}. Unknown entity type.`);
                        return;
                }

                onSuccess?.();
            } catch (error) {
                const err = error as Error;
                const operation = entityData.existingId ? 'associate' : entityData.id ? 'update' : 'create';
                console.error(`AdminProvider: error saving ${entityType}:`, err);
                if (!wasNotified(err))
                    notifyError(`Failed to ${operation} ${entityType}. ${err.message || 'Please try again.'}`);
            } finally {
                decPending();
            }
        },
        [fetchData, incPending, decPending]
    );

    const handleDeleteEntity = useCallback(
        async (entityId: number, entityType: DeletableEntityType) => {
            incPending();

            try {
                switch (entityType) {
                    case 'user':
                        await deleteUser(entityId);
                        await fetchData('users');
                        break;
                    case 'agency':
                        await deleteAgency(entityId);
                        await Promise.all([fetchData('agencies'), fetchData('unassociatedAgencies')]);
                        break;
                    case 'client':
                        await deleteCustomer(entityId);
                        await Promise.all([fetchData('clients'), fetchData('unassociatedClients')]);
                        break;
                    default:
                        console.error(`AdminProvider: unknown entity type for deletion: ${entityType}`);
                        notifyError(`Unable to delete ${entityType}. Unknown entity type.`);
                        return;
                }
            } catch (error) {
                const err = error as Error;
                console.error(`AdminProvider: error deleting ${entityType}:`, err);
                if (!wasNotified(err))
                    notifyError(`Failed to delete ${entityType}. ${err.message || 'Please try again.'}`);
            } finally {
                decPending();
            }
        },
        [fetchData, incPending, decPending]
    );

    return (
        <AdminContext.Provider
            value={{ adminData, loading, fetchData, fetchCompleteEntitySet, handleDeleteEntity, handleSaveEntity }}
        >
            {children}
        </AdminContext.Provider>
    );
};
